import React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";

// Import all icons from the free pack
import {
  Calendar01Icon, Calendar02Icon, Calendar03Icon, CalendarCheckIn01Icon,
  CalendarRemove01Icon, CalendarUpload01Icon,
  CheckmarkCircle02Icon, Tick02Icon, CheckmarkBadge01Icon,
  Sun01Icon, Sun03Icon, SunCloudAngledRain01Icon,
  Comment01Icon, Comment02Icon, Chat01Icon,
  Wallet01Icon, Wallet02Icon,
  Invoice01Icon, Invoice02Icon,
  Package01Icon, Package02Icon,
  Restaurant01Icon, Restaurant02Icon,
  School01Icon, GraduateMaleIcon,
  Mail01Icon, Mail02Icon, MailIcon,
  Clock01Icon, Clock02Icon, Timer01Icon,
  HealthIcon, StethoscopeIcon, Stethoscope02Icon,
  Folder01Icon, Folder02Icon, FolderOpenIcon,
  StarIcon, Award01Icon,
  AddressBookIcon, ContactIcon,
  Camera01Icon, Camera02Icon,
  News01Icon, NewsIcon,
  Location01Icon, Location02Icon, Location03Icon,
  UserGroup02Icon, UserMultiple02Icon,
  CrownIcon, Crown02Icon,
  Menu01Icon, Menu02Icon,
  ArrowLeft01Icon, ArrowLeft02Icon,
  ArrowRight01Icon, ArrowRight02Icon,
  ArrowDown01Icon, ArrowDown02Icon,
  ArrowUp01Icon,
  CogIcon, Setting06Icon,
  PlusSignIcon, PlusSignCircleIcon,
  Cancel01Icon, Cancel02Icon,
  Tick01Icon,
  SentIcon,
  Delete01Icon, Delete02Icon,
  Search01Icon, Search02Icon,
  PencilEdit01Icon, PencilEdit02Icon,
  UserGroup03Icon, UserAdd01Icon,
  PartyIcon, BirthdayCakeIcon,
  ShoppingCart01Icon, ShoppingCart02Icon,
  Home01Icon, Home02Icon, House01Icon,
  GridIcon, Layout01Icon,
  LockIcon, SquareLock01Icon,
  Notification01Icon, Notification02Icon, Notification03Icon,
  InformationCircleIcon,
  Alert01Icon, Alert02Icon,
  Logout01Icon, Logout02Icon,
  UserIcon, User02Icon,
  EyeIcon, ViewIcon,
  ViewOffIcon, ViewOffSlashIcon,
  Share01Icon, Share02Icon, ShareIcon,
  Attachment01Icon, Attachment02Icon,
  Image01Icon, Image02Icon,
  Refresh01Icon,
  LinkIcon, Link01Icon,
  CloudUploadIcon, Upload01Icon,
  File01Icon, File02Icon,
  PaintBrush01Icon, ColorsIcon,
  Hamburger01Icon,
  Briefcase01Icon,
  BankIcon,
  CreditCardIcon,
  Dollar01Icon, PoundIcon,
  SparklesIcon,
  Bookmark01Icon, Bookmark02Icon,
  CallIcon,
  GiftIcon, GiftCard02Icon,
  HourglassIcon,
  MinusSignIcon, MinusSignCircleIcon,
  SmileIcon, Happy01Icon,
  Flag01Icon,
  Megaphone01Icon,
  FootballIcon,
  BookOpen01Icon, BookOpen02Icon,
  DentalBrokenToothIcon,
  Tv01Icon,
  MusicNote01Icon,
  Wifi01Icon,
  Film01Icon,
  Tree01Icon,
  CloudIcon,
  Car01Icon, Bus01Icon,
  Baby01Icon,
  TShirtIcon,
  Chart01Icon, Analytics01Icon,
  Airplane01Icon,
  Award02Icon,
  ConstructionIcon, Wrench01Icon,
  Leaf01Icon, Leaf02Icon,
  FlowerIcon,
  IceCream01Icon,
  EggIcon,
  BatteryFullIcon, BatteryMedium01Icon, BatteryLowIcon, BatteryEmptyIcon,
  BatteryCharging01Icon,
  ClipboardIcon,
  Shield01Icon, SecurityCheckIcon,
  HelpCircleIcon, QuestionIcon,
  GlobeIcon, Globe02Icon,
  Moon01Icon, Moon02Icon,
  Store01Icon,
  Copy01Icon, CopyIcon,
  ToggleOnIcon, ToggleOffIcon,
  DashboardBrowsingIcon,
  Key01Icon, KeyIcon,
  Luggage01Icon,
  InboxIcon,
  Download01Icon,
  Drag01Icon,
  PoundCircleIcon,
  CurrencyIcon,
  ZapIcon, EnergyIcon, FlashIcon,
  PowerIcon,
  TextIcon,
  DocumentAttachmentIcon,
  BowlingIcon,
  CakeSliceIcon,
  RecordIcon,
} from "@hugeicons/core-free-icons";

// Map from Material Design / legacy names to Hugeicons
const iconMap: Record<string, IconSvgElement> = {
  // Navigation
  chevron_left: ArrowLeft02Icon,
  chevron_right: ArrowRight02Icon,
  expand_more: ArrowDown02Icon,
  expand_less: ArrowUp01Icon,
  arrow_back: ArrowLeft01Icon,
  arrow_back_ios: ArrowLeft01Icon,
  arrow_forward: ArrowRight01Icon,
  arrow_downward: ArrowDown01Icon,
  menu: Menu01Icon,

  // Core
  home: Home01Icon,
  calendar_month: Calendar01Icon,
  calendar_today: Calendar02Icon,
  event: Calendar03Icon,
  event_note: CalendarCheckIn01Icon,
  event_busy: CalendarRemove01Icon,
  event_upcoming: Calendar02Icon,
  date_range: Calendar03Icon,
  check_circle: CheckmarkCircle02Icon,
  check: Tick02Icon,
  done: Tick01Icon,
  done_all: CheckmarkBadge01Icon,
  wb_sunny: Sun01Icon,
  chat_bubble: Comment01Icon,
  chat_bubble_outline: Comment02Icon,
  chat: Chat01Icon,
  account_balance_wallet: Wallet01Icon,
  receipt_long: Invoice01Icon,
  inventory_2: Package01Icon,
  inventory: Package02Icon,
  restaurant: Restaurant01Icon,
  set_meal: Restaurant02Icon,
  school: School01Icon,
  mail: Mail01Icon,
  email: Mail02Icon,
  schedule: Clock01Icon,
  access_time: Clock02Icon,
  local_hospital: HealthIcon,
  folder_open: FolderOpenIcon,
  star: StarIcon,
  contacts: AddressBookIcon,
  photo_camera: Camera01Icon,
  add_a_photo: Camera02Icon,
  dynamic_feed: News01Icon,
  location_on: Location01Icon,
  place: Location02Icon,
  room: Location03Icon,
  map: Location01Icon,
  people: UserGroup02Icon,
  group: UserMultiple02Icon,
  workspace_premium: CrownIcon,
  format_list_bulleted: Menu02Icon,
  settings: CogIcon,
  add: PlusSignIcon,
  add_circle: PlusSignCircleIcon,
  close: Cancel01Icon,
  send: SentIcon,
  delete: Delete01Icon,
  delete_forever: Delete02Icon,
  search: Search01Icon,
  edit: PencilEdit01Icon,
  edit_square: PencilEdit02Icon,
  group_add: UserAdd01Icon,
  person_add: UserAdd01Icon,
  celebration: PartyIcon,
  shopping_cart: ShoppingCart01Icon,
  add_shopping_cart: ShoppingCart02Icon,
  grid_view: GridIcon,
  lock: LockIcon,
  notifications: Notification01Icon,
  notifications_active: Notification02Icon,
  notifications_off: Notification03Icon,
  info: InformationCircleIcon,
  warning: Alert01Icon,
  priority_high: Alert02Icon,
  logout: Logout01Icon,
  person: UserIcon,
  person_off: User02Icon,
  visibility: EyeIcon,
  visibility_off: ViewOffIcon,
  share: Share01Icon,
  attach_file: Attachment01Icon,
  image: Image01Icon,
  photo_library: Image02Icon,
  refresh: Refresh01Icon,
  repeat: Refresh01Icon,
  link: LinkIcon,
  cloud_upload: CloudUploadIcon,
  upload_file: Upload01Icon,
  file_present: File01Icon,
  description: File02Icon,
  color_lens: ColorsIcon,
  palette: PaintBrush01Icon,
  timer: Timer01Icon,
  fastfood: Hamburger01Icon,
  lunch_dining: Hamburger01Icon,
  local_grocery_store: ShoppingCart01Icon,
  shopping_basket: ShoppingCart02Icon,
  category: GridIcon,
  family_restroom: UserGroup03Icon,
  account_balance: BankIcon,
  credit_card: CreditCardIcon,
  payments: Dollar01Icon,
  currency_pound: PoundIcon,
  auto_awesome: SparklesIcon,
  bookmark_border: Bookmark01Icon,
  bookmark: Bookmark02Icon,
  call: CallIcon,
  phone_iphone: CallIcon,
  card_giftcard: GiftIcon,
  construction: ConstructionIcon,
  content_copy: Copy01Icon,
  flag: Flag01Icon,
  folder_off: Folder02Icon,
  hail: UserIcon,
  hourglass_bottom: HourglassIcon,
  inbox: InboxIcon,
  mood: SmileIcon,
  more_horiz: Menu01Icon,
  more_vert: Menu01Icon,
  favorite: StarIcon,
  remove: MinusSignIcon,
  verified_user: SecurityCheckIcon,
  campaign: Megaphone01Icon,
  task_alt: CheckmarkCircle02Icon,
  add_task: CheckmarkCircle02Icon,
  download: Download01Icon,
  upload: Upload01Icon,
  radio_button_unchecked: RecordIcon,
  history: Clock02Icon,
  push_pin: Bookmark02Icon,
  add_reaction: SmileIcon,

  // Data icons (Budget, Pantry, etc.)
  directions_bus: Bus01Icon,
  directions_car: Car01Icon,
  local_cafe: Restaurant02Icon,
  eco: Leaf01Icon,
  recycling: Leaf02Icon,
  nutrition: Leaf02Icon,
  spa: FlowerIcon,
  icecream: IceCream01Icon,
  egg_alt: EggIcon,
  grain: Leaf01Icon,
  ramen_dining: Restaurant01Icon,
  soup_kitchen: Restaurant02Icon,
  checkroom: TShirtIcon,
  sports_soccer: FootballIcon,
  sports_rugby: FootballIcon,
  sports_tennis: BowlingIcon,
  sports_motorsports: Car01Icon,
  rss_feed: Wifi01Icon,
  local_fire_department: FlashIcon,
  water_drop: CloudIcon,
  fitness_center: Award02Icon,
  health_and_safety: Shield01Icon,
  document_scanner: ClipboardIcon,
  menu_book: BookOpen01Icon,
  stethoscope: StethoscopeIcon,
  dentistry: DentalBrokenToothIcon,
  vaccines: HealthIcon,
  shield: Shield01Icon,
  badge: CreditCardIcon,
  article: File01Icon,
  handyman: Wrench01Icon,
  tv: Tv01Icon,
  music_note: MusicNote01Icon,
  wifi: Wifi01Icon,
  movie: Film01Icon,
  nature: Tree01Icon,
  cloud: CloudIcon,
  child_care: Baby01Icon,
  emergency: Alert01Icon,
  work: Briefcase01Icon,
  battery_full: BatteryFullIcon,
  battery_5_bar: BatteryFullIcon,
  battery_4_bar: BatteryMedium01Icon,
  battery_2_bar: BatteryLowIcon,
  battery_1_bar: BatteryEmptyIcon,
  bolt: ZapIcon,
  analytics: Analytics01Icon,
  insights: Chart01Icon,
  flight: Airplane01Icon,
  cake: BirthdayCakeIcon,
  park: Tree01Icon,
  emoji_events: Award01Icon,

  // Settings / theme
  dark_mode: Moon01Icon,
  light_mode: Sun01Icon,
  settings_brightness: Sun03Icon,
  dashboard_customize: Layout01Icon,
  privacy_tip: Shield01Icon,
  help_outline: HelpCircleIcon,
  help: QuestionIcon,
  language: GlobeIcon,
  storefront: Store01Icon,
  toggle_on: ToggleOnIcon,
  toggle_off: ToggleOffIcon,
  checklist: Menu02Icon,
  laptop: Tv01Icon,
  key: KeyIcon,
  luggage: Luggage01Icon,
  feedback: Comment02Icon,
  how_to_vote: CheckmarkBadge01Icon,
  sticky_note_2: ClipboardIcon,
  note: ClipboardIcon,
  ballot: CheckmarkBadge01Icon,
};

export interface IconProps {
  name: string;
  size?: number;
  color?: string;
  weight?: "bold" | "fill" | "duotone" | "regular";
  className?: string;
  style?: React.CSSProperties;
}

const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  color,
  className,
  style,
}) => {
  const iconData = iconMap[name];

  if (!iconData) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Icon] Missing icon mapping for: "${name}"`);
    }
    return (
      <span
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          ...style,
        }}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
        ...style,
      }}
      aria-hidden="true"
    >
      <HugeiconsIcon
        icon={iconData}
        size={size}
        color={color}
        strokeWidth={1.5}
      />
    </span>
  );
};

export default React.memo(Icon);
